/**
 * AlertsPage - Dedicated page for viewing all system alerts
 * 
 * Features:
 * - Filter by severity and category
 * - Bulk actions (mark all read, dismiss all)
 * - Manual scan trigger
 * - Pagination
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Bell, RefreshCw, Filter, CheckCheck, Trash2, 
  AlertCircle, AlertTriangle, Info, ChevronDown 
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  getAlerts,
  getAlertsSummary,
  scanForAlerts,
  markAlertRead,
  dismissAlert,
  markAllAlertsRead
} from '../api/alerts';
import AlertCard from '../components/alerts/AlertCard';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const AlertsPage = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    severity: null,
    category: null,
    showDismissed: false
  });
  const [page, setPage] = useState(0);
  const limit = 20;

  // Get summary
  const { data: summaryData } = useQuery({
    queryKey: ['alertsSummary'],
    queryFn: getAlertsSummary
  });

  // Get alerts with filters
  const { data: alertsData, isLoading } = useQuery({
    queryKey: ['alerts', filters.severity, filters.category, filters.showDismissed, page],
    queryFn: () => getAlerts({
      severity: filters.severity,
      category: filters.category,
      isDismissed: filters.showDismissed,
      limit,
      offset: page * limit
    }),
    staleTime: 0,
    refetchOnMount: true,
    keepPreviousData: false
  });

  // Mutations
  const scanMutation = useMutation({
    mutationFn: scanForAlerts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertsSummary'] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    }
  });

  const markReadMutation = useMutation({
    mutationFn: markAlertRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertsSummary'] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    }
  });

  const dismissMutation = useMutation({
    mutationFn: dismissAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertsSummary'] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    }
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllAlertsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertsSummary'] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    }
  });

  const summary = summaryData?.data || { critical: 0, warning: 0, info: 0, total: 0 };
  const alerts = alertsData?.data?.alerts || [];
  const total = alertsData?.data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const categories = [
    { value: null, label: 'All Categories' },
    { value: 'pricing', label: 'Pricing' },
    { value: 'pipeline', label: 'Pipeline' },
    { value: 'data_quality', label: 'Data Quality' },
    { value: 'lifecycle', label: 'Lifecycle' },
    { value: 'goals', label: 'Goals' }
  ];

  const severities = [
    { value: null, label: 'All Severities', icon: Filter },
    { value: 'critical', label: 'Critical', icon: AlertCircle, color: 'text-red-500' },
    { value: 'warning', label: 'Warning', icon: AlertTriangle, color: 'text-yellow-500' },
    { value: 'info', label: 'Info', icon: Info, color: 'text-blue-500' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <Bell className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">System Alerts</h1>
                <p className="text-sm text-gray-500">Anomaly detection and proactive notifications</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => scanMutation.mutate()}
                disabled={scanMutation.isPending}
              >
                <RefreshCw className={cn("h-4 w-4 mr-2", scanMutation.isPending && "animate-spin")} />
                {scanMutation.isPending ? 'Scanning...' : 'Scan Now'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => markAllReadMutation.mutate()}
                disabled={summary.total === 0}
              >
                <CheckCheck className="h-4 w-4 mr-2" />
                Mark All Read
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mt-4">
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="text-2xl font-bold text-gray-900">{summary.total}</div>
              <div className="text-xs text-gray-500">Total Alerts</div>
            </div>
            <div className="bg-red-50 rounded-lg p-3 border border-red-200">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <div className="text-2xl font-bold text-red-700">{summary.critical}</div>
              </div>
              <div className="text-xs text-red-600">Critical</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                <div className="text-2xl font-bold text-yellow-700">{summary.warning}</div>
              </div>
              <div className="text-xs text-yellow-600">Warning</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div className="flex items-center gap-2">
                <Info className="h-5 w-5 text-blue-500" />
                <div className="text-2xl font-bold text-blue-700">{summary.info}</div>
              </div>
              <div className="text-xs text-blue-600">Info</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-200">
          <span className="text-sm font-medium text-gray-700">Filter by:</span>
          
          {/* Severity Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                {severities.find(s => s.value === filters.severity)?.label || 'All Severities'}
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Severity</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {severities.map((sev) => (
                <DropdownMenuItem
                  key={sev.value || 'all'}
                  onClick={() => {
                    setFilters(f => ({ ...f, severity: sev.value }));
                    setPage(0);  // Reset to first page when filter changes
                  }}
                  className={cn(
                    filters.severity === sev.value && "bg-gray-100"
                  )}
                >
                  <sev.icon className={cn("h-4 w-4 mr-2", sev.color)} />
                  {sev.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Category Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                {categories.find(c => c.value === filters.category)?.label || 'All Categories'}
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Category</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {categories.map((cat) => (
                <DropdownMenuItem
                  key={cat.value || 'all'}
                  onClick={() => {
                    setFilters(f => ({ ...f, category: cat.value }));
                    setPage(0);  // Reset to first page when filter changes
                  }}
                  className={cn(
                    filters.category === cat.value && "bg-gray-100"
                  )}
                >
                  {cat.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Show Dismissed Toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer ml-auto">
            <input
              type="checkbox"
              checked={filters.showDismissed}
              onChange={(e) => setFilters(f => ({ ...f, showDismissed: e.target.checked }))}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Show dismissed
          </label>
        </div>
      </div>

      {/* Alerts List */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <Bell className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">No alerts found</h3>
            <p className="text-sm text-gray-500 mt-1">
              {filters.severity || filters.category 
                ? 'Try adjusting your filters' 
                : 'Your system is running smoothly!'}
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => scanMutation.mutate()}
              disabled={scanMutation.isPending}
            >
              <RefreshCw className={cn("h-4 w-4 mr-2", scanMutation.isPending && "animate-spin")} />
              Run a scan
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {alerts.map((alert) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onMarkRead={(id) => markReadMutation.mutate(id)}
                onDismiss={(id) => dismissMutation.mutate(id)}
              />
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsPage;
